import { Injectable } from '@angular/core';
import { SignalGroups, SignalItem } from '../../models/ucontrol-config.model';
import {
  CanMapDocument,
  CanMapPlatformSignal,
  CanMapPlatformUcmdmBlock,
  CanMapTcuUcmdmBlock,
  GeneratedUcontrolBlocks,
  SignalDiffResult,
} from '../models/can-map-block.model';
import {
  DEFAULT_DST,
  DEFAULT_SIGNAL_GAIN,
  DEFAULT_SIGNAL_OFFSET,
  UCMDM_RAW_TEMPLATE,
  UCMDM_SIGNAL_NAME,
  UCRTC_FIXED_BLOCKS,
  UCRTC_SIGNAL_NAME,
  computeTxPeriodMs,
  freqToMs,
} from '../constants/can-map-signal-templates.constants';

/**
 * Logica pura (nessuna chiamata HTTP) per generare i blocchi CAN Map "ucmdm"/"ucrtc"
 * a partire dalla configurazione UControl corrente, e per fonderli in modo idempotente
 * dentro un documento CAN Map esistente.
 */
@Injectable({ providedIn: 'root' })
export class CanMapGeneratorService {

  /**
   * Genera i blocchi a partire da tutti i segnali con tele===true, aggregando
   * eventuali più idCentralina in un unico blocco ucmdm (il marker è fisso e unico
   * per CAN Map, non può esistere un blocco per centralina).
   * Ritorna null se non ci sono segnali con tele===true (nessun blocco da generare).
   */
  generateBlocksFromSignalGroups(signalGroups: SignalGroups): GeneratedUcontrolBlocks | null {
    const teleSignals = this.collectTeleSignals(signalGroups);
    if (teleSignals.length === 0) {
      return null;
    }

    // tx_period_ms è determinato dal segnale più lento (frequenza massima in secondi)
    const representativeFreq = Math.max(...teleSignals.map(s => s.freq));

    const ucmdm: CanMapTcuUcmdmBlock = {
      scope: 'tcu',
      signal_name: UCMDM_SIGNAL_NAME,
      hfm: true,
      proto: 'RAW',
      tx_period_ms: computeTxPeriodMs(representativeFreq),
      on_request: true,
      RAW: { ...UCMDM_RAW_TEMPLATE },
      ucParmId: teleSignals.map(s => ({
        dst: s.idCentralina ?? DEFAULT_DST,
        par: s.idx,
        reqPeriod: freqToMs(s.freq),
      })),
    };

    const ucmdmPlatform: CanMapPlatformUcmdmBlock = {
      scope: 'platform',
      ref: UCMDM_SIGNAL_NAME,
      signals: teleSignals.map(s => ({
        par: s.idx,
        name: s.codice,
        offset: DEFAULT_SIGNAL_OFFSET,
        gain: DEFAULT_SIGNAL_GAIN,
        unit: s.unit,
      })),
    };

    return {
      ucmdm,
      ucmdmPlatform,
      ucrtc: UCRTC_FIXED_BLOCKS.tcu,
      ucrtcPlatform: UCRTC_FIXED_BLOCKS.platform,
    };
  }

  findExistingUcmdmTcu(data: any[]): any | null {
    return data.find(b => b?.scope === 'tcu' && b?.signal_name === UCMDM_SIGNAL_NAME) ?? null;
  }

  findExistingUcmdmPlatform(data: any[]): any | null {
    return data.find(b => b?.scope === 'platform' && b?.ref === UCMDM_SIGNAL_NAME) ?? null;
  }

  findExistingUcrtcTcu(data: any[]): any | null {
    return data.find(b => b?.scope === 'tcu' && b?.signal_name === UCRTC_SIGNAL_NAME) ?? null;
  }

  findExistingUcrtcPlatform(data: any[]): any | null {
    return data.find(b => b?.scope === 'platform' && b?.ref === UCRTC_SIGNAL_NAME) ?? null;
  }

  /**
   * Applica il merge idempotente su una COPIA del documento (mai sull'originale):
   * - i blocchi ucmdm (tcu+platform) vengono SEMPRE sostituiti interamente (o aggiunti se assenti)
   * - i blocchi ucrtc (tcu+platform) vengono aggiunti SOLO se non già presenti
   */
  mergeIntoCanMapDocument(doc: CanMapDocument, generated: GeneratedUcontrolBlocks): CanMapDocument {
    const result: CanMapDocument = { ...doc, data: [...(doc.data ?? [])] };

    const replaceOrPush = (predicate: (b: any) => boolean, newBlock: any) => {
      const idx = result.data.findIndex(predicate);
      if (idx >= 0) {
        result.data[idx] = newBlock;
      } else {
        result.data.push(newBlock);
      }
    };

    replaceOrPush(b => b?.scope === 'tcu' && b?.signal_name === UCMDM_SIGNAL_NAME, generated.ucmdm);
    replaceOrPush(b => b?.scope === 'platform' && b?.ref === UCMDM_SIGNAL_NAME, generated.ucmdmPlatform);

    if (!this.findExistingUcrtcTcu(result.data)) {
      result.data.push(generated.ucrtc);
    }
    if (!this.findExistingUcrtcPlatform(result.data)) {
      result.data.push(generated.ucrtcPlatform);
    }

    return result;
  }

  /**
   * Rimuove da una COPIA del documento (mai dall'originale) tutti e 4 i blocchi introdotti da
   * questo modulo (ucmdm + ucrtc, tcu+platform). Usato quando la configurazione non ha più
   * segnali con tele===true: il modulo UControl è responsabile dell'intero set di blocchi che
   * ha introdotto, quindi se non c'è più nulla da mappare li rimuove tutti.
   */
  removeUcontrolBlocks(doc: CanMapDocument): CanMapDocument {
    const isUcontrolBlock = (b: any) =>
      (b?.scope === 'tcu' && (b?.signal_name === UCMDM_SIGNAL_NAME || b?.signal_name === UCRTC_SIGNAL_NAME)) ||
      (b?.scope === 'platform' && (b?.ref === UCMDM_SIGNAL_NAME || b?.ref === UCRTC_SIGNAL_NAME));

    return { ...doc, data: (doc.data ?? []).filter(b => !isUcontrolBlock(b)) };
  }

  /**
   * Confronto mirato (non un diff JSON generico) tra i segnali/tx_period_ms già presenti nella
   * CAN Map e quelli del blocco proposto: evidenzia solo segnali aggiunti, segnali rimossi, e
   * cambio di tx_period_ms. Nessun confronto su gain/offset: vengono comunque sempre resettati
   * ai default alla generazione, quindi segnalarli come "cambiati" ogni volta sarebbe rumore
   * visivo poco utile.
   */
  computeSignalDiff(
    existingSignals: CanMapPlatformSignal[] | null,
    existingTxPeriodMs: number | null,
    generatedSignals: CanMapPlatformSignal[],
    generatedTxPeriodMs: number,
  ): SignalDiffResult {
    const existingPars = new Set((existingSignals ?? []).map(s => s.par));
    const generatedPars = new Set(generatedSignals.map(s => s.par));

    return {
      newPars: new Set(generatedSignals.filter(s => !existingPars.has(s.par)).map(s => s.par)),
      removedSignals: (existingSignals ?? []).filter(s => !generatedPars.has(s.par)),
      txPeriodChanged: existingTxPeriodMs !== null && existingTxPeriodMs !== generatedTxPeriodMs,
    };
  }

  private collectTeleSignals(signalGroups: SignalGroups): SignalItem[] {
    const teleSignals: SignalItem[] = [];
    for (const signals of Object.values(signalGroups)) {
      for (const signal of signals) {
        if (signal.tele === true) {
          teleSignals.push(signal);
        }
      }
    }
    return teleSignals;
  }
}
